import { useEffect, useRef, useState, type ReactNode } from 'react';

interface LargeTitleHeaderProps {
  /** Page title. Renders large + bold, collapsing into a compact sticky bar on scroll. */
  title: string;
  /** Optional small caption shown above the large title. */
  subtitle?: string;
  /** Right-aligned action(s) — buttons, menus, etc. Visible in both states. */
  actions?: ReactNode;
  /** Show a back chevron; called on tap. */
  onBack?: () => void;
  /** Label announced for the back button. */
  backLabel?: string;
  /**
   * Element whose scroll position drives the collapse. Defaults to `window`.
   * Pass a ref when the page scrolls inside a container rather than the body.
   */
  scrollRef?: React.RefObject<HTMLElement>;
  /** Scroll distance (px) over which the large title fades into the compact bar. */
  collapseDistance?: number;
  /** Optional content rendered under the large title (e.g. a segmented control / search). */
  children?: ReactNode;
  className?: string;
}

/**
 * iOS-style large-title navigation header. Shows a big bold title that scrolls
 * away while a compact, translucent sticky bar fades in with the same title.
 * Reduced-motion friendly (no transforms, just opacity). Desktop and mobile.
 */
export function LargeTitleHeader({
  title,
  subtitle,
  actions,
  onBack,
  backLabel = 'Back',
  scrollRef,
  collapseDistance = 52,
  children,
  className = '',
}: LargeTitleHeaderProps) {
  const [progress, setProgress] = useState(0); // 0 = expanded, 1 = collapsed
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const target: HTMLElement | Window = scrollRef?.current ?? window;
    const read = () =>
      scrollRef?.current
        ? scrollRef.current.scrollTop
        : window.scrollY || document.documentElement.scrollTop;

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const p = Math.min(1, Math.max(0, read() / collapseDistance));
        setProgress(p);
      });
    };

    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [scrollRef, collapseDistance]);

  const collapsed = progress > 0.5;

  return (
    <header className={`relative ${className}`}>
      {/* Compact sticky bar — fades in as the large title scrolls away. */}
      <div
        className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white/80 backdrop-blur-xl border-b transition-[border-color] duration-200"
        style={{ borderColor: collapsed ? 'rgba(0,0,0,0.08)' : 'transparent' }}
      >
        <div className="flex h-12 items-center gap-2">
          {onBack && (
            <button
              onClick={onBack}
              aria-label={backLabel}
              className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-primary active:bg-primary/10 transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back_ios_new</span>
            </button>
          )}
          <h2
            className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900 transition-opacity duration-200"
            style={{ opacity: collapsed ? 1 : 0, pointerEvents: collapsed ? 'auto' : 'none' }}
            aria-hidden={!collapsed}
          >
            {title}
          </h2>
          <div className="ml-auto flex items-center gap-1">{actions}</div>
        </div>
      </div>

      {/* Large title — translates up and fades as you scroll. */}
      <div
        className="px-0 pt-2 pb-3 transition-[opacity,transform] duration-200 will-change-transform"
        style={{
          opacity: 1 - progress,
          transform: `translateY(${-progress * 6}px)`,
        }}
      >
        {subtitle && (
          <p className="text-xs font-semibold uppercase tracking-wide text-primary/80">{subtitle}</p>
        )}
        <h1 className="text-[1.75rem] font-extrabold leading-tight tracking-tight text-gray-900">
          {title}
        </h1>
      </div>

      {children && <div className="pb-2">{children}</div>}
    </header>
  );
}
